import { campaignService } from "./campaignService";
import { supabase } from "../supabase/supabase";
import {
  ExtractedData,
  LeadUpload,
  LeadUploadInsert,
  LeadUploadStatus,
} from "../types/leadUpload";

export const leadUploadService = {
  /**
   * Check if the extracted data has a valid phone number
   */
  hasValidPhoneNumber(extractedData: any): boolean {
    // Valid phone checks
    if (!extractedData) return false;

    // Check common phone field names
    const possiblePhoneFields = [
      "phone",
      "phone_number",
      "phoneNumber",
      "mobile",
      "cell",
      "contact",
      "tel",
      "telephone",
    ];

    // Check if any phone field has a valid value (non-null, non-empty string)
    for (const field of possiblePhoneFields) {
      if (
        extractedData[field] &&
        typeof extractedData[field] === "string" &&
        extractedData[field].trim() !== ""
      ) {
        // Do simple validation to check if it's likely a phone number (has digits)
        const digitsOnly = extractedData[field].replace(/\D/g, "");
        if (digitsOnly.length >= 6) {
          // Most phone numbers have at least 6 digits
          console.log(
            `Found valid phone in field ${field}: ${extractedData[field]}`,
          );
          return true;
        }
      }
    }

    console.log("No valid phone number found in extracted data");
    return false;
  },
  /**
   * Upload a file to Supabase storage and create a lead_uploads record
   */
  async uploadFile(
    file: File,
    organizationId: string,
    campaignId?: string,
  ): Promise<{ data: LeadUpload | null; error: Error | null }> {
    try {
      // Check if this file has been uploaded before by filename
      const { data: existingUploads } = await supabase
        .from("lead_uploads")
        .select("id, file_path, md_content")
        .eq("organization_id", organizationId)
        .eq("original_filename", file.name)
        .order("created_at", { ascending: false })
        .limit(1);

      let mdContent = null;

      // If we found a previous upload with the same filename, try to reuse the MD content
      if (existingUploads && existingUploads.length > 0 && existingUploads[0].md_content) {
        mdContent = existingUploads[0].md_content;
        console.log("Reusing MD content from previous upload");
      }

      // 1. Upload file to Supabase storage
      const fileExt = file.name.split(".").pop();
      const filePath = `${organizationId}/${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 15)}.${fileExt}`;

      const { data: storageData, error: storageError } = await supabase.storage
        .from("lead-uploads")
        .upload(filePath, file);

      if (storageError) {
        throw storageError;
      }

      // 2. Create lead_uploads record
      const leadUpload: LeadUploadInsert = {
        organization_id: organizationId,
        original_filename: file.name,
        file_path: filePath,
        upload_status: "pending",
        md_content: mdContent,
        campaign_id: campaignId || null,
      };

      const { data, error } = await supabase
        .from("lead_uploads")
        .insert(leadUpload)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error uploading file:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Store MD content for a lead upload
   */
  async storeMdContent(
    leadUploadId: string,
    mdContent: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("lead_uploads")
        .update({ md_content: mdContent })
        .eq("id", leadUploadId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error storing MD content:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Get all lead uploads for an organization
   */
  async getLeadUploads(
    organizationId: string,
  ): Promise<{ data: LeadUpload[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting lead uploads:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get lead uploads with a specific status
   */
  async getLeadUploadsByStatus(
    organizationId: string,
    status: LeadUploadStatus,
  ): Promise<{ data: LeadUpload[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("upload_status", status)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error(`Error getting lead uploads with status ${status}:`, error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get lead uploads for a specific campaign
   */
  async getLeadUploadsByCampaign(
    campaignId: string,
  ): Promise<{ data: LeadUpload[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error(
        `Error getting lead uploads for campaign ${campaignId}:`,
        error,
      );
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get lead uploads for a specific campaign template
   */
  async getLeadUploadsByCampaignTemplate(
    campaignTemplateId: string,
  ): Promise<{ data: LeadUpload[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("campaign_template_id", campaignTemplateId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error(
        `Error getting lead uploads for campaign template ${campaignTemplateId}:`,
        error,
      );
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update the status of a lead upload
   */
  async updateLeadUploadStatus(
    id: string,
    status: LeadUploadStatus,
  ): Promise<{ data: LeadUpload | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .update({ upload_status: status, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error updating lead upload status:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update the extracted data for a lead upload
   */
  async updateExtractedData(
    id: string,
    extractedData: ExtractedData,
  ): Promise<{ data: LeadUpload | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .update({
          extracted_data: extractedData,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error updating extracted data:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Process a lead upload by sending it to the extraction API
   */
  async processLeadUpload(
    leadUploadId: string,
  ): Promise<{ data: ExtractedData | null; error: Error | null }> {
    try {
      console.log(`Starting to process lead upload with ID: ${leadUploadId}`);
      // 1. Get the lead upload record
      const { data: leadUpload, error: getError } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("id", leadUploadId)
        .single();

      if (getError || !leadUpload) {
        console.error("Failed to get lead upload:", getError);
        throw getError || new Error("Lead upload not found");
      }

      console.log(`Found lead upload record:`, {
        id: leadUpload.id,
        filename: leadUpload.original_filename,
        status: leadUpload.upload_status,
        hasMdContent: !!leadUpload.md_content,
      });

      // 2. Update status to processing
      await this.updateLeadUploadStatus(leadUploadId, "processing");

      // Try the URL approach first
      console.log(`Getting signed URL for file: ${leadUpload.file_path}`);
      const { url: signedUrl } = await this.getFileDownloadUrl(
        leadUpload.file_path,
      );

      let response;

      if (signedUrl) {
        // 4. Try to send the URL directly to the extraction API first
        console.log("Sending URL to extraction API...");
        try {
          response = await fetch(
            "https://hari-resume-mdconvert.leiusn.easypanel.host/convert/extract-info-from-url",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                accept: "application/json",
              },
              body: JSON.stringify({ url: signedUrl }),
            },
          );

          if (response.ok) {
            console.log("URL-based extraction successful");
          } else {
            console.error(
              `URL-based extraction failed with status ${response.status}`,
            );
            throw new Error(
              `API request failed with status ${response.status}`,
            );
          }
        } catch (urlError) {
          console.error(
            "URL-based extraction failed, falling back to file upload:",
            urlError,
          );
          response = null;
        }
      } else {
        console.log(
          "Could not generate URL, falling back to file upload method",
        );
      }

      // If URL approach failed, fall back to file upload
      if (!response || !response.ok) {
        console.log("Falling back to file upload method");

        // 3b. Download the file from storage
        console.log(`Downloading file from storage: ${leadUpload.file_path}`);
        const { data: fileData, error: fileError } = await supabase.storage
          .from("lead-uploads")
          .download(leadUpload.file_path);

        if (fileError || !fileData) {
          console.error("Failed to download file from storage:", fileError);
          await this.updateLeadUploadStatus(leadUploadId, "failed");
          throw fileError || new Error("File not found");
        }

        // 4b. Create form data for the API request
        const formData = new FormData();
        formData.append("file", fileData, leadUpload.original_filename);

        // 5b. Send the file to the extraction API
        console.log("Sending file to extraction API using direct upload...");
        response = await fetch(
          "https://hari-resume-mdconvert.leiusn.easypanel.host/convert/extract-info",
          {
            method: "POST",
            body: formData,
          },
        );
      }

      if (!response.ok) {
        console.error(`API request failed with status ${response.status}`);
        await this.updateLeadUploadStatus(leadUploadId, "failed");
        throw new Error(`API request failed with status ${response.status}`);
      }

      // 5. Parse the response
      let extractedData: ExtractedData;
      let rawMarkdownContent = "";

      try {
        const responseText = await response.text();
        console.log("Raw API response:", responseText);
        const parsedResponse = JSON.parse(responseText);
        console.log("Parsed response from API:", parsedResponse);

        // Save markdown content
        rawMarkdownContent = parsedResponse.markdown_content || "";

        // Map the new response format to our expected format
        if (parsedResponse.personal_info) {
          extractedData = {
            ...parsedResponse.personal_info,
            // Ensure our expected field names are set
            first_name: parsedResponse.personal_info.first_name || "",
            last_name: parsedResponse.personal_info.last_name || "",
            email: parsedResponse.personal_info.email || "",
            phone: parsedResponse.personal_info.phone_number || "",
          };
        } else {
          // Fallback for unexpected format
          extractedData = parsedResponse;
        }

        console.log("Mapped extracted data:", extractedData);
      } catch (parseError) {
        console.error("Error parsing API response:", parseError);
        await this.updateLeadUploadStatus(leadUploadId, "failed");
        throw new Error(`Failed to parse API response: ${parseError.message}`);
      }

      // Store the MD content for future use
      console.log(
        "Storing markdown content:",
        rawMarkdownContent.substring(0, 100) + "...",
      );
      await this.storeMdContent(leadUploadId, rawMarkdownContent);

      // 7. Update the lead upload with the extracted data
      // Check for phone in both phone and phone_number fields
      const hasValidPhoneNumber = this.hasValidPhoneNumber(extractedData);

      console.log(`Has valid phone number: ${hasValidPhoneNumber}`, {
        phone: extractedData.phone,
        phone_number: extractedData.phone_number,
      });

      // If phone_number exists but phone doesn't, copy it to phone field
      if (!extractedData.phone && extractedData.phone_number) {
        extractedData.phone = extractedData.phone_number;
        console.log("Copied phone_number to phone field");
      }

      const newStatus: LeadUploadStatus = hasValidPhoneNumber
        ? "completed"
        : "needs_review";

      console.log(`Setting upload status to: ${newStatus}`);
      await this.updateLeadUploadStatus(leadUploadId, newStatus);
      await this.updateExtractedData(leadUploadId, extractedData);

      // Always try to create a lead automatically, regardless of status
      console.log("Attempting to automatically create lead from upload");
      try {
        const { success, leadId } =
          await this.createLeadFromUpload(leadUploadId);
        console.log(
          `Auto-create lead result: ${success ? "success" : "failed"}`,
          { leadId },
        );

        if (success && leadId) {
          console.log(`Successfully created lead with ID: ${leadId}`);

          // If we have campaign_id from the upload, ensure the lead is added to the campaign
          if (leadUpload.campaign_id) {
            console.log(
              `Ensuring lead is added to campaign: ${leadUpload.campaign_id}`,
            );
            try {
              const { error: campaignError } = await supabase
                .from("campaign_leads")
                .insert({
                  campaign_id: leadUpload.campaign_id,
                  lead_id: leadId,
                });
              // The unique constraint already ensures we don't add duplicates

              if (campaignError) {
                console.error(
                  "Error adding lead to campaign:",
                  campaignError,
                );
              } else {
                console.log(
                  `Successfully added lead ${leadId} to campaign ${leadUpload.campaign_id}`,
                );
              }
            } catch (campaignError) {
              console.error(
                "Exception adding lead to campaign:",
                campaignError,
              );
            }
          }
          
          // If we have campaign_template_id from the upload, ensure the lead is added to the campaign template
          if (leadUpload.campaign_template_id) {
            console.log(
              `Ensuring lead is added to campaign template: ${leadUpload.campaign_template_id}`,
            );
            try {
              const { error: campaignError } = await supabase
                .from("campaign_template_leads")
                .insert({
                  campaign_template_id: leadUpload.campaign_template_id,
                  lead_id: leadId,
                });
              // The unique constraint already ensures we don't add duplicates

              if (campaignError) {
                console.error(
                  "Error adding lead to campaign template:",
                  campaignError,
                );
              } else {
                console.log(
                  `Successfully added lead ${leadId} to campaign template ${leadUpload.campaign_template_id}`,
                );
              }
            } catch (campaignError) {
              console.error(
                "Exception adding lead to campaign template:",
                campaignError,
              );
            }
          }
        } else {
          console.log("Lead creation failed or returned no ID");
        }
      } catch (createError) {
        console.error("Error auto-creating lead:", createError);
        // Don't throw here, just log the error and continue
      }

      return { data: extractedData, error: null };
    } catch (error) {
      console.error("Error processing lead upload:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Extract and validate lead data from extracted data and manual input
   */
  validateAndTransformLeadData(
    extractedData: any,
    manualData?: Partial<ExtractedData>,
    organizationId?: string,
    leadUploadId?: string,
  ): { validatedData: any; isValid: boolean; validationErrors: string[] } {
    // Combine extracted data with manual data if provided
    const leadData = { ...extractedData, ...manualData };
    const validationErrors: string[] = [];

    console.log("Starting lead data validation with:", leadData);

    // Validate and transform first name
    if (!leadData.first_name && !leadData.name) {
      console.log("First name is missing, using default");
      validationErrors.push("First name is missing");
      // Use default value
      leadData.first_name = "Unknown";
    } else if (!leadData.first_name && leadData.name) {
      // Extract first name from full name
      console.log(`Extracting first name from full name: ${leadData.name}`);
      leadData.first_name = leadData.name.split(" ")[0] || "";
    }

    // Validate and transform last name
    if (
      !leadData.last_name &&
      (!leadData.name || !leadData.name.includes(" "))
    ) {
      console.log("Last name is missing, using default");
      validationErrors.push("Last name is missing");
      // Use default value
      leadData.last_name = "User";
    } else if (
      !leadData.last_name &&
      leadData.name &&
      leadData.name.includes(" ")
    ) {
      // Extract last name from full name
      console.log(`Extracting last name from full name: ${leadData.name}`);
      leadData.last_name = leadData.name.split(" ").slice(1).join(" ") || "";
    }

    // Look for phone in various properties
    console.log("Checking for phone number in data");
    const possiblePhoneFields = [
      "phone",
      "phone_number",
      "phoneNumber",
      "mobile",
      "cell",
      "contact",
      "tel",
      "telephone",
    ];

    let foundPhone = false;
    for (const field of possiblePhoneFields) {
      if (
        leadData[field] &&
        typeof leadData[field] === "string" &&
        leadData[field].trim() !== ""
      ) {
        // Do simple validation to check if it's likely a phone number (has digits)
        const digitsOnly = leadData[field].replace(/\D/g, "");
        if (digitsOnly.length >= 6) {
          // Most phone numbers have at least 6 digits
          console.log(
            `Found valid phone in field ${field}: ${leadData[field]}`,
          );
          leadData.phone = leadData[field];
          foundPhone = true;
          break;
        } else {
          console.log(
            `Field ${field} contains value but doesn't look like a valid phone: ${leadData[field]}`,
          );
        }
      }
    }

    // Try to find phone in the markdown content if available
    if (!foundPhone && leadData.markdown_content) {
      console.log("Trying to extract phone from markdown content");
      const phoneRegex =
        /(\+\d{1,3})?[\s-]?\(?(\d{3})\)?[\s-]?(\d{3})[\s-]?(\d{4})/g;
      const matches = [...leadData.markdown_content.matchAll(phoneRegex)];

      if (matches.length > 0) {
        console.log(
          `Found ${matches.length} potential phone numbers in markdown`,
        );
        leadData.phone = matches[0][0];
        foundPhone = true;
      }
    }

    // Validate phone number
    if (!foundPhone) {
      console.log("No phone number found in any field");
      validationErrors.push("Phone number is required");

      if (manualData && manualData.phone) {
        // If manual data provides a phone, use it
        leadData.phone = manualData.phone;
        console.log(`Using manually provided phone number: ${leadData.phone}`);
      } else {
        // In production, you should return isValid: false here
        // For testing purposes, generate a dummy number
        leadData.phone = `+1234567${Math.floor(Math.random() * 10000)
          .toString()
          .padStart(4, "0")}`;
        console.log(`Generated dummy phone number: ${leadData.phone}`);

        // SET THIS TO isValid: false for production
        return {
          validatedData: leadData,
          isValid: false,
          validationErrors,
        };
      }
    }

    // Prepare the lead object for database insertion
    const leadToInsert = {
      organization_id: organizationId,
      first_name: leadData.first_name || "",
      last_name: leadData.last_name || "",
      email: leadData.email || "",
      phone_number: leadData.phone, // Use phone_number field to match database schema
      lead_upload_id: leadUploadId,
    };

    return {
      validatedData: leadToInsert,
      isValid: true,
      validationErrors,
    };
  },

  /**
   * Create a lead from extracted data
   */
  async createLeadFromUpload(
    leadUploadId: string,
    manualData?: Partial<ExtractedData>,
  ): Promise<{ success: boolean; leadId?: string; error: Error | null }> {
    try {
      console.log(`Creating lead from upload ID: ${leadUploadId}`, {
        manualData: !!manualData,
      });
      // 1. Get the lead upload record
      const { data: leadUpload, error: getError } = await supabase
        .from("lead_uploads")
        .select("*")
        .eq("id", leadUploadId)
        .single();

      if (getError || !leadUpload) {
        console.error("Failed to get lead upload:", getError);
        throw getError || new Error("Lead upload not found");
      }

      console.log(`Found lead upload record for lead creation:`, {
        id: leadUpload.id,
        filename: leadUpload.original_filename,
        status: leadUpload.upload_status,
        hasExtractedData: !!leadUpload.extracted_data,
      });

      // Ensure we have the organization ID from the lead upload
      if (!leadUpload.organization_id) {
        console.error("Organization ID is missing from lead upload");
        throw new Error("Organization ID is required to create a lead");
      }

      // 2. Validate and transform lead data
      const extractedData = leadUpload.extracted_data || {};
      const {
        validatedData: leadToInsert,
        isValid,
        validationErrors,
      } = this.validateAndTransformLeadData(
        extractedData,
        manualData,
        leadUpload.organization_id,
        leadUploadId,
      );

      if (!isValid) {
        console.error("Validation failed:", validationErrors);
        throw new Error(validationErrors.join(", "));
      }

      console.log("Inserting lead with data:", leadToInsert);

      // First check if a lead with this phone number already exists for this organization
      console.log(
        "Checking for existing lead with same phone_number and organization_id",
      );
      const { data: existingLead, error: checkError } = await supabase
        .from("leads")
        .select("id, first_name, last_name, email, lead_upload_id")
        .eq("phone_number", leadToInsert.phone_number)
        .eq("organization_id", leadToInsert.organization_id)
        .maybeSingle();

      let insertData;
      let insertError;

      if (checkError) {
        console.error("Error checking for existing lead:", checkError);
        insertError = checkError;
      } else if (existingLead) {
        console.log(
          "Found existing lead with same phone number, updating it:",
          existingLead,
        );

        // Update the existing lead
        const { data: updateData, error: updateError } = await supabase
          .from("leads")
          .update({
            first_name: leadToInsert.first_name,
            last_name: leadToInsert.last_name,
            email: leadToInsert.email,
            lead_upload_id: leadToInsert.lead_upload_id,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingLead.id)
          .select()
          .single();

        insertData = updateData;
        insertError = updateError;
      } else {
        console.log("No existing lead found, inserting new lead");
        // Insert a new lead
        const { data: newData, error: newError } = await supabase
          .from("leads")
          .insert(leadToInsert)
          .select()
          .single();

        insertData = newData;
        insertError = newError;
      }

      if (insertError) {
        console.error("Error inserting lead into database:", insertError);
        throw insertError;
      }

      console.log("Lead created successfully:", insertData);

      // 5. Update the lead upload status to completed
      await this.updateLeadUploadStatus(leadUploadId, "completed");

      // 6. If the lead upload is associated with a campaign, add the lead to the campaign
      if (leadUpload.campaign_id && insertData) {
        console.log(
          `Adding lead to campaign: ${leadUpload.campaign_id}`,
        );
        try {
          // Check if the lead is already associated with this campaign
          const { data: existingCampaignLead, error: checkError } =
            await supabase
              .from("campaign_leads")
              .select("id")
              .eq("campaign_id", leadUpload.campaign_id)
              .eq("lead_id", insertData.id)
              .maybeSingle();

          if (checkError) {
            console.error(
              "Error checking existing campaign lead:",
              checkError,
            );
          }

          // Only insert if not already exists
          if (!existingCampaignLead) {
            const { error: campaignError } = await supabase
              .from("campaign_leads")
              .insert({
                campaign_id: leadUpload.campaign_id,
                lead_id: insertData.id,
              });

            if (campaignError) {
              console.error(
                "Error adding lead to campaign:",
                campaignError,
              );
              // Continue even if there's an error adding to campaign
            } else {
              console.log("Successfully added lead to campaign");
            }
          } else {
            console.log(
              "Lead is already associated with this campaign, skipping insertion",
            );
          }
        } catch (campaignError) {
          console.error(
            "Exception adding lead to campaign:",
            campaignError,
          );
          // Continue even if there's an exception
        }
      }
      
      // 7. If the lead upload is associated with a campaign template, add the lead to the campaign template
      if (leadUpload.campaign_template_id && insertData) {
        console.log(
          `Adding lead to campaign template: ${leadUpload.campaign_template_id}`,
        );
        try {
          // Check if the lead is already associated with this campaign template
          const { data: existingCampaignTemplateLead, error: checkError } =
            await supabase
              .from("campaign_template_leads")
              .select("id")
              .eq("campaign_template_id", leadUpload.campaign_template_id)
              .eq("lead_id", insertData.id)
              .maybeSingle();

          if (checkError) {
            console.error(
              "Error checking existing campaign template lead:",
              checkError,
            );
          }

          // Only insert if not already exists
          if (!existingCampaignTemplateLead) {
            const { error: campaignError } = await supabase
              .from("campaign_template_leads")
              .insert({
                campaign_template_id: leadUpload.campaign_template_id,
                lead_id: insertData.id,
              });

            if (campaignError) {
              console.error(
                "Error adding lead to campaign template:",
                campaignError,
              );
              // Continue even if there's an error adding to campaign template
            } else {
              console.log("Successfully added lead to campaign template");
            }
          } else {
            console.log(
              "Lead is already associated with this campaign template, skipping insertion",
            );
          }
        } catch (campaignError) {
          console.error(
            "Exception adding lead to campaign template:",
            campaignError,
          );
          // Continue even if there's an exception
        }
      } else if (insertData) {
        // Check if the upload has campaign_template_id set to null but there was a campaign_template_id provided in manual data
        console.log(
          "Checking for manual campaign template assignment in extracted data",
        );

        if (manualData && manualData.campaign_template_id) {
          const campaignTemplateId = manualData.campaign_template_id;
          console.log(
            `Manual campaign template ID found: ${campaignTemplateId}, adding lead to this campaign template`,
          );

          try {
            // Check if the lead already exists in this campaign template
            const { data: existingCampaignTemplateLead, error: checkError } =
              await supabase
                .from("campaign_template_leads")
                .select("id")
                .eq("campaign_template_id", campaignTemplateId)
                .eq("lead_id", insertData.id)
                .maybeSingle();

            if (checkError) {
              console.error(
                "Error checking existing campaign template lead:",
                checkError,
              );
            }

            // Only insert if not already exists
            if (!existingCampaignTemplateLead) {
              const { error: campaignError } = await supabase
                .from("campaign_template_leads")
                .insert({
                  campaign_template_id: campaignTemplateId,
                  lead_id: insertData.id,
                });

              if (campaignError) {
                console.error(
                  "Error adding lead to manually specified campaign template:",
                  campaignError,
                );
              } else {
                console.log(
                  "Successfully added lead to manually specified campaign template",
                );
              }
            } else {
              console.log(
                "Lead is already associated with this campaign template, skipping insertion",
              );
            }
          } catch (campaignError) {
            console.error(
              "Exception adding lead to manually specified campaign template:",
              campaignError,
            );
          }
        } else {
          console.log(
            "No campaign template ID associated with this lead upload",
          );
        }
      }

      // After successful lead creation, check for campaign_id
      if (leadUpload.campaign_id && insertData) {
        console.log(
          `Adding lead to campaign: ${leadUpload.campaign_id}`,
        );
        try {
          const { error: campaignError } = await campaignService.addLeadsToCampaign(
            leadUpload.campaign_id,
            [insertData.id],
          );

          if (campaignError) {
            console.error(
              "Error adding lead to campaign:",
              campaignError,
            );
            // Continue even if there's an error adding to campaign
          } else {
            console.log("Successfully added lead to campaign");
          }
        } catch (campaignError) {
          console.error(
            "Exception adding lead to campaign:",
            campaignError,
          );
          // Continue even if there's an exception
        }
      }

      return { success: true, leadId: insertData?.id, error: null };
    } catch (error) {
      console.error("Error creating lead from upload:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Get MD content for a lead upload
   */
  async getMdContent(
    leadUploadId: string,
  ): Promise<{ data: any; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("lead_uploads")
        .select("md_content, extracted_data")
        .eq("id", leadUploadId)
        .single();

      if (error) {
        throw error;
      }

      let mdContent = data.md_content;
      let extractedData = data.extracted_data;

      // Try to parse MD content if it looks like JSON (backward compatibility)
      if (
        mdContent &&
        (mdContent.startsWith("{") || mdContent.startsWith("["))
      ) {
        try {
          const parsedContent = JSON.parse(mdContent);
          if (parsedContent.markdown_content) {
            mdContent = parsedContent.markdown_content;
          }
        } catch (e) {
          // If parsing fails, use the content as-is
          console.log(
            "MD content is not JSON or couldn't be parsed, using as-is",
          );
        }
      }

      // Return both the markdown content and extracted data for display
      return {
        data: {
          mdContent: mdContent,
          extractedData: extractedData,
        },
        error: null,
      };
    } catch (error) {
      console.error("Error getting MD content:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get a download URL for a file in Supabase storage
   */
  async getFileDownloadUrl(
    filePath: string,
  ): Promise<{ url: string | null; error: Error | null }> {
    try {
      console.log(`Creating signed URL for file path: ${filePath}`);

      // Check if file exists first
      const { data: fileExists, error: checkError } = await supabase.storage
        .from("lead-uploads")
        .list(filePath.split("/").slice(0, -1).join("/"), {
          limit: 100,
          offset: 0,
          sortBy: { column: "name", order: "asc" },
        });

      if (checkError) {
        console.error("Error checking if file exists:", checkError);
      } else {
        console.log(
          `Found ${fileExists.length} files in directory:`,
          fileExists.map((f) => f.name),
        );

        const fileName = filePath.split("/").pop();
        const fileFound = fileExists.some((f) => f.name === fileName);
        console.log(`File ${fileName} found: ${fileFound}`);

        if (!fileFound) {
          throw new Error(`File ${fileName} not found in storage`);
        }
      }

      // Try to get a public URL instead of a signed URL
      const publicUrl = supabase.storage
        .from("lead-uploads")
        .getPublicUrl(filePath).data.publicUrl;

      console.log(`Generated public URL: ${publicUrl}`);

      // Also try signed URL as backup
      try {
        const { data, error } = await supabase.storage
          .from("lead-uploads")
          .createSignedUrl(filePath, 3600); // URL valid for 1 hour for better reliability

        if (error) {
          console.error("Error creating signed URL:", error);
        } else {
          console.log(`Generated signed URL: ${data.signedUrl}`);
          return { url: data.signedUrl, error: null };
        }
      } catch (signedUrlError) {
        console.error("Exception creating signed URL:", signedUrlError);
      }

      // Return public URL if signed URL fails
      return { url: publicUrl, error: null };
    } catch (error) {
      console.error("Error getting file download URL:", error);
      return { url: null, error: error as Error };
    }
  },

  /**
   * Get the file content as text (for text-based files)
   */
  async getFileContent(
    filePath: string,
  ): Promise<{ content: string | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.storage
        .from("lead-uploads")
        .download(filePath);

      if (error || !data) {
        throw error || new Error("File not found");
      }

      // Convert blob to text
      const content = await data.text();
      return { content, error: null };
    } catch (error) {
      console.error("Error getting file content:", error);
      return { content: null, error: error as Error };
    }
  },
};
